/**
 * The forced-update policy as the backend serves it (GET /api/app/version-policy): per platform a minimum and latest
 * version, an update link and a message. The comparison matches the server's, so the app and the API agree on who
 * is outdated. Anything unreadable means "no minimum" - a policy problem never blocks a learner.
 */
const VERSION = /^\d{1,6}(\.\d{1,6}){0,3}$/;

export function compareVersions(a, b) {
  if (!VERSION.test(String(a)) || !VERSION.test(String(b))) return null;
  const left = String(a).split('.').map(Number);
  const right = String(b).split('.').map(Number);
  for (let i = 0; i < Math.max(left.length, right.length); i++) {
    const diff = (left[i] || 0) - (right[i] || 0);
    if (diff !== 0) return diff < 0 ? -1 : 1;
  }
  return 0;
}

export function platformPolicy(policy, platform) {
  const entry = policy && policy.platforms && policy.platforms[platform];
  return entry && typeof entry === 'object' ? entry : null;
}

/** True only for a known, comparable version below the platform's minimum. */
export function isBelowMinimum(policy, platform, version) {
  const entry = platformPolicy(policy, platform);
  if (!entry || !entry.minimum) return false;
  const order = compareVersions(version, entry.minimum);
  return order !== null && order < 0;
}

/** The policy, or null when it cannot be read (then nothing is enforced on this client). */
export async function fetchUpdatePolicy(baseUrl, fetchImpl = fetch) {
  try {
    const response = await fetchImpl(`${baseUrl}/api/app/version-policy`, {cache: 'no-store'});
    if (!response.ok) return null;
    const body = await response.json();
    return body && body.data && typeof body.data === 'object' ? body.data : null;
  } catch (_) {
    return null;
  }
}
